import { Button, Col, Input, Row, Select, Typography } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";
import { LOYALTY_FIELD_LIMITS, REWARD_UNIT, REWARD_UNIT_OPTIONS } from "features/LoyaltyPackage/core/constants";
import {
  LoyaltyConfirmDialog,
  LOYALTY_CONFIRM_ICON,
  LoyaltySection,
} from "features/LoyaltyPackage/presentation/components";
import { calcMilestoneMaxQty, formatAmountInput, parseAmountInput } from "features/LoyaltyPackage/core/utils";
import { t } from "i18next";
import { useState } from "react";

const { Text } = Typography;

const cx = (...classes) => classes.filter(Boolean).join(" ");

function FieldError({ message }) {
  if (!message) return null;
  return <div className="package-form-milestone-error">{message}</div>;
}

function MilestoneSection({
  className,
  milestones,
  milestoneErrors = [],
  onMilestoneChange,
  onAddMilestone,
  onRemoveMilestone,
}) {
  const [removeIndex, setRemoveIndex] = useState(null);

  const handleRemoveConfirm = () => {
    if (removeIndex != null) {
      onRemoveMilestone(removeIndex);
      setRemoveIndex(null);
    }
  };

  return (
    <LoyaltySection className={className} title={t("loyaltyPackage.form.milestoneSection")}>
      {milestones.map((milestone, index) => {
        const maxQty = calcMilestoneMaxQty(milestones, index);
        const isLast = index === milestones.length - 1;
        const errors = milestoneErrors[index] || {};

        return (
          <div key={milestone.id} className="package-form-milestone-item">
            <div className="package-form-milestone-item-header">
              <div className="package-form-milestone-item-title">
                <Text>{t("loyaltyPackage.packageForm.milestoneLabel", { index: index + 1 })}</Text>
                <PlusCircleOutlined className="package-form-milestone-add-icon" onClick={() => onAddMilestone(index)} />
              </div>
              <Button
                type="link"
                className="package-form-milestone-delete"
                disabled={milestones.length <= LOYALTY_FIELD_LIMITS.MIN_MILESTONES}
                onClick={() => setRemoveIndex(index)}
              >
                {t("loyaltyPackage.milestone.removeMilestone")}
              </Button>
            </div>

            <Row gutter={[24, 0]}>
              <Col xs={24} md={12}>
                <div className="package-form-field">
                  <label className="package-form-label">
                    {t("loyaltyPackage.packageForm.minQtyLabel")} <span className="loyalty-required">*</span>
                  </label>
                  <Input
                    className={cx("package-form-input-full", errors.minQty && "package-form-input-error")}
                    placeholder={t("loyaltyPackage.packageForm.minQtyPlaceholder")}
                    value={formatAmountInput(milestone.minQty)}
                    onChange={(e) => onMilestoneChange(index, "minQty", parseAmountInput(e.target.value))}
                  />
                  <FieldError message={errors.minQty} />
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className="package-form-field">
                  <label className="package-form-label">{t("loyaltyPackage.packageForm.maxQtyLabel")}</label>
                  <Input
                    className="package-form-input-full package-form-field-readonly"
                    readOnly
                    value={isLast ? "" : (maxQty ?? "")}
                    placeholder=""
                  />
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className="package-form-field">
                  <label className="package-form-label">
                    {t("loyaltyPackage.milestone.rewardUnit")} <span className="loyalty-required">*</span>
                  </label>
                  <Select
                    className={cx("package-form-input-full", errors.rewardUnit && "package-form-input-error")}
                    placeholder={t("loyaltyPackage.packageForm.rewardUnitPlaceholder")}
                    options={REWARD_UNIT_OPTIONS}
                    value={milestone.rewardUnit}
                    onChange={(val) => onMilestoneChange(index, "rewardUnit", val)}
                  />
                  <FieldError message={errors.rewardUnit} />
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className="package-form-field">
                  <label className="package-form-label">
                    {milestone.rewardUnit === REWARD_UNIT.HAP
                      ? t("loyaltyPackage.packageForm.rewardAmountHap")
                      : t("loyaltyPackage.packageForm.rewardAmountVnd")}{" "}
                    <span className="loyalty-required">*</span>
                  </label>
                  <Input
                    className={cx("package-form-input-full", errors.rewardAmount && "package-form-input-error")}
                    placeholder={t("loyaltyPackage.packageForm.rewardAmountPlaceholder")}
                    value={formatAmountInput(milestone.rewardAmount)}
                    onChange={(e) => onMilestoneChange(index, "rewardAmount", parseAmountInput(e.target.value))}
                  />
                  <FieldError message={errors.rewardAmount} />
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className="package-form-field">
                  <label className="package-form-label">
                    {t("loyaltyPackage.packageForm.displayPercentLabel")} <span className="loyalty-required">*</span>
                  </label>
                  <Input
                    className={cx("package-form-input-full", errors.displayPercent && "package-form-input-error")}
                    placeholder={t("loyaltyPackage.packageForm.displayPercentPlaceholder")}
                    value={formatAmountInput(milestone.displayPercent)}
                    onChange={(e) => onMilestoneChange(index, "displayPercent", parseAmountInput(e.target.value))}
                  />
                  <FieldError message={errors.displayPercent} />
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className="package-form-field">
                  <label className="package-form-label">
                    {t("loyaltyPackage.packageForm.awardAmountTitleLabel")} <span className="loyalty-required">*</span>
                  </label>
                  <Input
                    className={cx("package-form-input-full", errors.awardAmountTitle && "package-form-input-error")}
                    placeholder={t("loyaltyPackage.packageForm.awardAmountTitlePlaceholder")}
                    value={milestone.awardAmountTitle ?? ""}
                    onChange={(e) => onMilestoneChange(index, "awardAmountTitle", e.target.value || undefined)}
                  />
                  <FieldError message={errors.awardAmountTitle} />
                </div>
              </Col>
            </Row>
          </div>
        );
      })}

      <LoyaltyConfirmDialog
        visible={removeIndex != null}
        iconType={LOYALTY_CONFIRM_ICON.DELETE}
        message={t("loyaltyPackage.packageForm.confirmRemoveMilestoneMessage")}
        onCancel={() => setRemoveIndex(null)}
        onConfirm={handleRemoveConfirm}
      />
    </LoyaltySection>
  );
}

export default MilestoneSection;
